import React, { createContext, Fragment, useCallback, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { mapIndexed, notBlank } from "../../util/ext";
import { pluck } from "ramda";
import { Box, Button, List, ListItem, ListItemText } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../theme";
import { Error, Warning, CheckCircle, Info } from "@mui/icons-material";

const AlertContext = createContext({
  showMessage: null,
  showMessages: null,
  alertError: null,
});

const AlertSeverity = {
  success: "success",
  error: "error",
  warning: "warning",
  default: "default",
};
const AlertProvider = props => {
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [severity, setSeverity] = useState(AlertSeverity.default);
  const [action, setAction] = useState(null);

  const handleClose = useCallback(() => {
    setVisible(false);
    setSeverity(AlertSeverity.default);
    setMessages([]);
    setAction(null);
  }, []);

  const showMessage = useCallback((message, severity, action = null) => {
    if (action) {
      setAction(action);
    }
    setSeverity(severity);
    setMessages([message]);
    setVisible(true);
  }, []);

  const showMessages = useCallback((messages, severity) => {
    setSeverity(severity);
    setMessages([messages]);
    setVisible(true);
  }, []);

  const alertError = useCallback(error => {
    setSeverity("error");
    if (notBlank(error.body)) {
      setMessages(pluck("message", error.body.errors));
    } else {
      setMessages([error.message]);
    }
    setVisible(true);
  }, []);

  const chooseMessageColor = () => {
    switch (severity) {
      case "error":
        return theme.palette.error.main;
      case "success":
        return theme.palette.success.main;
      case "warn":
      case "warning":
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const choseIcon = () => {
    switch (severity) {
      case "error":
        return (
          <Error
            aria-label="error"
            style={{ fontSize: "4.8rem", color: theme.palette.error.main }}
          />
        );
      case "success":
        return (
          <CheckCircle
            aria-label="success"
            style={{ fontSize: "4.8rem", color: theme.palette.success.main }}
          />
        );
      case "warn":
      case "warning":
        return (
          <Warning
            aria-label="warning"
            style={{ fontSize: "4.8rem", color: theme.palette.warning.main }}
          />
        );
      default:
        return (
          <Info
            aria-label="info"
            style={{ fontSize: "4.8rem", color: theme.palette.primary.main }}
          />
        );
    }
  };

  return (
    <AlertContext.Provider value={{ showMessage, showMessages, alertError }}>
      <Fragment>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={visible}
          onClose={handleClose}
        >
          <Box>
            {notBlank(messages) && (
              <Box
                minWidth={400}
                minHeight={100}
                padding={5}
                bgcolor="white"
                boxShadow={5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>{choseIcon()}</Box>
                <List dense>
                  {messages &&
                    mapIndexed(
                      (message, idx) => (
                        <ListItem key={`message:${idx}`}>
                          <ListItemText
                            primaryTypographyProps={{
                              variant: "h2",
                              style: { color: chooseMessageColor() },
                            }}
                            primary={message}
                          />
                        </ListItem>
                      ),
                      messages,
                    )}
                </List>
                {notBlank(action) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      action.onClick();
                    }}
                  >
                    {action?.label ?? "Undo"}
                  </Button>
                )}
                <IconButton onClick={handleClose} size="large">
                  <CloseIcon fontSize="large" fontWeight="bold" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Snackbar>
      </Fragment>
      {props.children}
    </AlertContext.Provider>
  );
};

export { AlertContext, AlertProvider, AlertSeverity };
